import { action, observable, computed } from 'mobx';
import { defaultsDeep } from 'lodash';

import { mapDataExplorerFilters } from '@kentik/ui-shared/nms/filters';
import $metrics from 'app/stores/metrics/$metrics';
import AbstractDataViewModel from 'app/stores/query/AbstractDataViewModel';
import { getObjectForHash } from 'app/stores/query/urlHash';
import { mergeFilterGroups } from 'core/util/filters';

export default class MetricsExplorerDataViewModel extends AbstractDataViewModel {
  @observable.ref
  query = {};

  /**
   * Hash for the effective query (including any filters applied via parametric overrides or
   * overlayFilters) so the panel title link navigates to Metrics Explorer with the correct state.
   * This is updated whenever initializeHashWithOverrides() is called, regardless of whether
   * parametric/guided-mode filters are active. Initially null before the first initialization.
   */
  @observable
  titleLinkHash = null;

  get isFlowVisualization() {
    return false;
  }

  get viewType() {
    return 'metrics';
  }

  get viewShowTitleLink() {
    // TODO: base on selectedMeasurement?
    return true;
  }

  @action
  setTitle = (title) => {
    this.dvTitle = title;
  };

  @action
  refresh = (saved_query_id) =>
    this.initializeHashWithOverrides(saved_query_id, this.overrides, {
      parametricOverrides: this.parametricOverrides
    });

  @action
  initializeHash(hash) {
    this.loading = true;
    this.hash = hash;

    return getObjectForHash(hash).then((query) => {
      Object.assign(query, { lastUpdated: Date.now() });
      this.query = query;
      this.setFullyLoaded();
      return query;
    });
  }

  /**
   * @param {string} hash
   * @param {object} overrides
   * @param {{ syncDepth?: boolean, overlayFilters?: boolean, parametricOverrides?: object }} [options]
   */
  @action
  initializeHashWithOverrides = async (hash, overrides = {}, { overlayFilters = true, parametricOverrides } = {}) => {
    this.loading = true;
    this.hash = hash;
    this.parametricOverrides = parametricOverrides;

    return getObjectForHash(hash).then((query) => {
      const { lookback_seconds, starting_time, ending_time } = overrides;
      let range = {};
      if (starting_time && ending_time) {
        query.starting_time = starting_time;
        query.ending_time = ending_time;
        query.lookback_seconds = 0;
        range = {
          start: starting_time,
          end: ending_time,
          lookback_seconds: 0,
          lookback: 'PT0S'
        };
      } else if (lookback_seconds) {
        range = {
          lookback: `PT${lookback_seconds}S` // start, end
        };
      }

      const newQuery = defaultsDeep(
        {
          lastUpdated: Date.now(),
          kmetrics: { range }
        },
        query
      );

      const measurement = $metrics.measurementModelByName(query.kmetrics.measurement)?.toJS();

      if (overlayFilters) {
        newQuery.kmetrics.filters = mergeFilterGroups(
          newQuery.kmetrics.filters,
          mapDataExplorerFilters(overrides.filters, measurement)
        );
        this.overrides = overrides;
      }

      if (parametricOverrides) {
        const existingFilters = query.kmetrics.filters;

        // Override the filter value for the specified filter field, leave other filters alone.
        if (parametricOverrides.specific && measurement) {
          existingFilters.filterGroups.forEach((group) => {
            group.filters.forEach((filter) => {
              const dimensionMetadata = measurement.storage.Dimensions[filter.filterField];
              if (
                filter.filterField === parametricOverrides.filterField ||
                dimensionMetadata.QueryColumn === parametricOverrides.filterField
              ) {
                filter.filterValue = parametricOverrides.filterValue;
              }
            });
          });
          newQuery.kmetrics.filters = mapDataExplorerFilters(existingFilters, measurement);
        } else {
          newQuery.kmetrics.filters = mapDataExplorerFilters(parametricOverrides.filters, measurement);
        }
      }

      this.query = newQuery;

      // Always keep titleLinkHash in sync with the effective query (including any parametric /
      // guided-mode filters that were applied via parametricOverrides or overlayFilters). This
      // covers all parametric modes: override_all, override_specific, and add_group – the last of
      // which applies its filter through overlayFilters without setting parametricOverrides.
      $metrics
        .queryToHash(newQuery)
        .then(
          action((titleHash) => {
            this.titleLinkHash = titleHash;
          })
        )
        .catch((error) => {
          console.error('Failed to generate title link hash:', error);
          // Fall back to the original hash if hash generation fails
          this.titleLinkHash = this.hash;
        });

      this.setFullyLoaded();
      return this.query;
    });
  };

  @computed
  get appliedFilters() {
    const { kmetrics: { filters } = {} } = this.query;
    return filters;
  }

  setUpdateFrequency(frequency = 0) {
    if (frequency) {
      this.updateFrequency = setInterval(this.refresh, frequency * 1000);
    } else if (this.updateFrequency) {
      clearInterval(this.updateFrequency);
      delete this.updateFrequency;
    }
  }

  destroy() {
    clearInterval(this.updateFrequency);
  }

  reflow() {
    if (this.component && this.component.reflow) {
      this.component.reflow();
    }
  }
}
